<script lang="ts" setup>
import {
  UIButton,
  UIForm,
  UIFormItem,
  UIModal,
  UIModalContent,
  UIModalFooter,
  UISelect,
  UIInputNumber,
  UISwitch,
  UITextSmMedium,
} from '@gohighlevel/ghl-ui'
import {
  BusinessProduct,
  BusinessProductPrice,
  BusinessProductPriceOption,
  ProductLineEvents,
  PriceTypeEnum,
  AddNewProductItem,
  BusinessProductOption,
} from '@gohighlevel/ghl-proposals-common'
import { computed, ref, withDefaults } from 'vue'
import { PlusIcon, Image01Icon } from '@gohighlevel/ghl-icons/24/outline'
import { useProductListStore } from '@/store/productListStore'
import { useCurrency, useProducts } from '@/composition'
import { debounce } from 'lodash'

const productListStore = useProductListStore()
const { getCurrencySymbol } = useCurrency()
const { loading: productsLoading, fetchBusinessProducts } = useProducts()

const props = withDefaults(
  defineProps<{
    productList: BusinessProductOption[]
    show: boolean
    loading: boolean
    elementVersion: number
  }>(),
  {
    show: false,
    loading: false,
    elementVersion: 1,
  }
)
const productsPriceList = ref<BusinessProductPriceOption[]>([])
const searchResults = ref<BusinessProduct[]>([])
const searchEnabled = ref(false)
const products = computed(() => {
  // If search is enabled, return search results else return the default product list
  const list = searchEnabled.value ? searchResults.value : props.productList
  return mutateProductListToOptions(list)
})

const formValue = ref<AddNewProductItem>({
  productId: '',
  priceId: '',
  setupFee: 0,
  priceType: '',
  showProductImageInList: false,
  imageUrl: '',
})
const searchText = ref('')
const currencySymbol = ref()
const currencyCode = ref()

const productOptions = computed(() => {
  return products.value.map((product: any) => {
    return {
      label: product.name,
      value: product._id,
      disabled: product.disabled,
    }
  })
})

const mutateProductListToOptions = (list: BusinessProduct[]) => {
  return list
    .map((product: BusinessProduct) => {
      if (!product.prices) return
      return {
        ...product,
        label: product.name,
        value: product._id,
        disabled: isProductDisabled(product),
        prices: product?.prices.map((price: BusinessProductPrice) => {
          return {
            label: price?.name,
            value: price?._id,
            disabled: isPriceDisabled(price),
            ...price,
          }
        }),
      }
    })
    .filter(Boolean) as BusinessProduct[]
}

const resetFormValue = () => {
  formValue.value = {
    productId: '',
    priceId: '',
    setupFee: 0,
    priceType: '',
    showProductImageInList: false,
    imageUrl: '',
  }
  productsPriceList.value = []
}

const clearQuery = () => {
  searchText.value = ''
}

const search = debounce(async (text: string) => {
  searchText.value = text
  searchEnabled.value = true
  if (text) {
    const response = await fetchBusinessProducts(text)
    searchResults.value = mutateProductListToOptions(response?.products)
  } else {
    searchResults.value = props.productList
  }
}, 300)

const handleSelectProduct = (value: string) => {
  formValue.value.productId = value
  formValue.value.priceId = ''
  productsPriceList.value = (props.productList?.find(
    item => item._id === formValue.value.productId
  )?.prices || []) as BusinessProductPriceOption[]
  if (productsPriceList.value.length >= 1) {
    formValue.value.priceId = productsPriceList.value[0]._id
    handleSelectProductPrice(formValue.value.priceId)
  }
}

const handleSelectProductPrice = (priceId: string) => {
  const price = productsPriceList.value.find(
    priceItem => priceItem._id === priceId
  )
  if (price) {
    currencySymbol.value = getCurrencySymbol(price.currency)
    currencyCode.value = price.currency
    formValue.value.priceType = price.type
    formValue.value.setupFee = price?.setupFee || 0
  }
}

function isProductDisabled(product: BusinessProduct) {
  if (productListStore.currency) {
    const showProduct = product?.prices?.some(
      (item: BusinessProductPrice) =>
        item.currency === productListStore.currency
    )
    return !showProduct
  }

  return false
}

function isPriceDisabled(price: BusinessProductPrice) {
  if (!productListStore.currency) {
    return false
  }
  return price.currency != productListStore.currency
}

const productImageSrc = computed(() => {
  return products.value.find(
    product => product._id === formValue.value.productId
  )?.image
})
const onToggleSwitch = (value: boolean) => {
  formValue.value.showProductImageInList = value
  formValue.value.imageUrl = productImageSrc.value || ''
}
</script>

<template>
  <UIModal :show="props.show">
    <template #header>
      <div class="py-2">
        <UIButton
          id="add-new-item-btn"
          class="border-0 w-full px-2 py-7 text-primary-700 rounded-lg bg-primary-50"
          type="tertiary"
          @click="() => $emit(ProductLineEvents.OPEN_ADD_PRODUCT_MODAL)"
        >
          <PlusIcon class="w-5 h-5 mr-2" />
          {{ $t('common.newItem') }}
        </UIButton>
      </div>
    </template>
    <UIModalContent>
      <UIForm id="add-product-form">
        <UIFormItem
          :label="$t('common.selectProduct')"
          path="productId"
          required
        >
          <UISelect
            class="product-select"
            filterable
            id="select-product-id"
            remote
            :size="'large'"
            :placeholder="$t('common.selectProduct')"
            :value="formValue.productId"
            :options="productOptions"
            :loading="productsLoading"
            :reset-menu-on-options-change="true"
            @update:value="handleSelectProduct"
            @search="search"
            @update:show="(show: boolean) => !show && clearQuery()"
            @focus="search('')"
            @click="search('')"
          />
        </UIFormItem>
        <UIFormItem :label="$t('common.price')" path="priceId" required>
          <UISelect
            id="products-price-item"
            v-model:value="formValue.priceId"
            :loading="props.loading"
            :options="productsPriceList"
            :placeholder="$t('common.selectPrice')"
            @change="handleSelectProductPrice"
          />
        </UIFormItem>

        <UIFormItem
          v-if="formValue.priceType === PriceTypeEnum.RECURRING"
          :label="$t('common.setupFee')"
          path="setupFee"
        >
          <div class="w-full">
            <UIInputNumber
              id="product-price-setupFee"
              v-model="formValue.setupFee"
              placeholder="0"
              :show-button="false"
              :precision="2"
              :min="0"
              size="large"
              class="w-full"
            >
              <template #prefix>{{ currencySymbol }}</template>
              <template #suffix>{{ currencyCode }}</template>
            </UIInputNumber>
          </div>
        </UIFormItem>

        <div class="flex items-center mb-4" v-if="props.elementVersion >= 3">
          <UISwitch
            @update:value="val => onToggleSwitch(val)"
            id="activeRow.optional"
            :value="formValue.showProductImageInList || false"
          />
          <UITextSmMedium class="ml-2">{{
            $t('common.showProductImageInList')
          }}</UITextSmMedium>
        </div>
        <div
          v-if="formValue.showProductImageInList"
          class="w-[120px] h-[120px] object-cover overflow-hidden rounded-sm border border-1"
        >
          <img
            v-if="productImageSrc"
            :src="productImageSrc"
            :alt="$t('common.productImageAlt')"
            class="max-w-full m-auto object-cover w-[120px] h-[120px]"
          />
          <Image01Icon v-else class="w-[120px] h-[120px] text-gray-300" />
        </div>
      </UIForm>
    </UIModalContent>
    <template #footer>
      <UIModalFooter
        id="add-new-product-modal-footer"
        :negative-text="$t('common.cancel')"
        :positive-text="$t('common.addItem')"
        @negativeClick="
          () => {
            $emit(ProductLineEvents.CLOSE_ADD_ITEM_MODAL)
            resetFormValue()
          }
        "
        @positiveClick="
          () => {
            $emit(ProductLineEvents.ADD_LINE_ITEM_TO_LIST, formValue)
            resetFormValue()
          }
        "
      />
    </template>
  </UIModal>
</template>

<style>
.n-modal-container {
  z-index: 2020 !important;
}
</style>
